import { Box, Button, Flex, Grid } from "@chakra-ui/react"
import { FormattedMessage } from "react-intl"

import FormattedDate from "@/components/FormattedDate"
import { FormattedPriceRange } from "@/components/FormattedPrice"
import { EnvelopeIcon } from "@/components/Icons"
import { RefLabel } from "@/components/RefLabel"
import SharedEntityName from "@/components/Sharing/SharedEntityName"
import { SharedDataID } from "@/components/Sharing/components/SharedDataID"
import { P3, P4 } from "@/components/Typographies"
import useGetUserRoles from "@/hooks/useGetUserRoles"
import type { SharedDataJsonldReadCollectionSharedData } from "@/networking/api"
import { isNotBlank, isNotNullNorUndefined } from "@/utils/ts-utils"

import { InterBUStatusTag } from "../InterBUStatus/InterBUStatusTag"
import { getInterBuStatusColors } from "../InterBUStatus/interbuStatusPalette"
import { InterBUTimeline } from "./InterBUTimeline"
import { type InterBUDirection, getCardActions, getCommentSummary } from "./cardRules"

// Compact action buttons: the theme's "sm" size (36px, 16px sides) is too heavy inside a card.
const CARD_BUTTON = { size: "sm", minH: "28px", px: "3", py: "0" } as const

export type ShareCardHandlers = {
  onOpenDetails: (id: string) => void
  // Confirmation modals of the actions (ADR 0009, D3, revised on 2026-10-05).
  onAccept: (id: string) => void
  onDecline: (id: string) => void
  onCancel: (id: string) => void
}

type Props = {
  share: SharedDataJsonldReadCollectionSharedData
  direction: InterBUDirection
} & ShareCardHandlers

// One share of the InterBU list (ADR 0009, D3).
export const InterBUShareCard: React.FC<Props> = ({
  share,
  direction,
  onOpenDetails,
  onAccept,
  onDecline,
  onCancel,
}) => {
  const { hasRoleBuAdmin } = useGetUserRoles()
  // Both sides (all) is the administrators' view: read only, the API refuses their answers and
  // withdrawals (guru_cannot_update), so no action is offered.
  const { canAnswer, canCancel } =
    direction === "all"
      ? { canAnswer: false, canCancel: false }
      : getCardActions(share, {
          isRecipientManager: hasRoleBuAdmin(share.toCluster?.id),
          isRequestor: direction === "sent",
        })
  const interBu = share.interBu
  const comments = getCommentSummary(share, direction)
  // The current status as a 2px rule on top of the card (DS InterBU palette).
  const statusColors = getInterBuStatusColors(share.status?.code)

  return (
    <Grid
      as="article"
      // Side status column from lg only: between md and lg it squeezed the timeline out of the card.
      templateColumns={{ base: "minmax(0, 1fr)", lg: "160px minmax(0, 1fr)" }}
      columnGap="8"
      rowGap="4"
      p="5"
      bg="surface"
      border="1px solid"
      borderColor="line"
      borderTop="2px solid"
      borderTopColor={statusColors?.fill ?? "line"}
      minW={0}
      data-testid="interbu-card"
    >
      <Flex direction="column" gap="2" alignItems="start" minW={0}>
        <InterBUStatusTag status={share.status?.code} isTinted />
        <P3 color="textMuted">
          <SharedDataID refID={interBu?.shortReference} />
        </P3>
      </Flex>

      <Flex direction="column" gap="3" minW={0}>
        <Box>
          <P3 display="block" fontWeight="semibold">
            <SharedEntityName item={share} />
          </P3>
          {/* "Real estate offer · Apartment · Avignon": project type, property type, city. */}
          <P3 display="block" color="textMuted">
            {[
              isNotNullNorUndefined(interBu?.type) && (
                <span key="type">
                  <RefLabel label={interBu?.type} />
                  {isNotNullNorUndefined(interBu?.subtype) && (
                    <>
                      {" "}
                      <RefLabel label={interBu?.subtype} />
                    </>
                  )}
                </span>
              ),
              isNotNullNorUndefined(interBu?.propertyType) && (
                <RefLabel key="property" label={interBu?.propertyType} />
              ),
              isNotBlank(interBu?.city) && <span key="city">{interBu?.city}</span>,
            ]
              .filter(Boolean)
              .flatMap((part, index) => (index === 0 ? [part] : [" · ", part]))}
          </P3>
        </Box>
        <P3>
          {share.fromCluster?.name} → {share.toCluster?.name}
          <P4 as="span" color="textMuted">
            {" · "}
            <FormattedMessage id="sharing.interbu.card.requestedOn" />{" "}
            <FormattedDate date={share.createdAt} />
          </P4>
        </P3>
        <InterBUTimeline share={share} />
        {comments.count > 0 && (
          <Flex
            alignItems="center"
            gap="2"
            data-testid="interbu-card-comments"
            data-from-other-side={comments.isFromOtherSide}
          >
            <EnvelopeIcon boxSize={3.5} color={comments.isFromOtherSide ? "accent" : "textMuted"} />
            <P4
              color={comments.isFromOtherSide ? "text" : "textMuted"}
              fontWeight={comments.isFromOtherSide ? "semibold" : "normal"}
            >
              <FormattedMessage
                id="sharing.interbu.card.comments"
                values={{ count: comments.count }}
              />
              {comments.lastAt && (
                <>
                  {" · "}
                  <FormattedMessage id="sharing.interbu.card.lastComment" />{" "}
                  <FormattedDate date={comments.lastAt} />
                </>
              )}
            </P4>
          </Flex>
        )}

        <Flex
          justifyContent="space-between"
          alignItems="center"
          gap="4"
          wrap="wrap"
          pt="3"
          borderTop="1px solid"
          borderColor="line"
        >
          <P3 fontWeight="semibold">
            <FormattedPriceRange
              min={interBu?.priceMin}
              max={interBu?.priceMax}
              currency={interBu?.priceUnit}
            />
          </P3>
          <Flex gap="2" wrap="wrap" alignItems="center">
            {canCancel && (
              <Button {...CARD_BUTTON} variant="outline" onClick={() => onCancel(share.id)}>
                <FormattedMessage id="sharing.interbu.card.cancel" />
              </Button>
            )}
            {canAnswer && (
              <>
                <Button {...CARD_BUTTON} variant="outline" onClick={() => onDecline(share.id)}>
                  <FormattedMessage id="sharing.interbu.card.decline" />
                </Button>
                <Button {...CARD_BUTTON} variant="solid" onClick={() => onAccept(share.id)}>
                  <FormattedMessage id="sharing.interbu.card.accept" />
                </Button>
              </>
            )}
            <Button size="sm" variant="link" onClick={() => onOpenDetails(share.id)}>
              <FormattedMessage id="sharing.interbu.card.details" />
            </Button>
          </Flex>
        </Flex>
      </Flex>
    </Grid>
  )
}
